import React, { Suspense, useState } from 'react';

import { Theme, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import makeStyles from '@mui/styles/makeStyles';

import {
  AppConfig,
  ContactFormType,
} from '../../../../core/api/whitelabel/whitelabel.types';
import { ProfileActionsType } from '../../../../core/types/menu.types';
import { menuUtils } from '../../../../utils';
import * as Api from '../../../../weborder/Api';
import { Typography } from '../../../atoms';
import { LoadingSpinner } from '../../../layouts';
import { ExpandSection } from '../../../molecules';
import { PaymentMethodsDialog } from '../..';
import { AddCreditCardDialog } from '../../AddCreditCardDialog/AddCreditCardDialog';
import { ProfileList } from '../../ProfileMenu/partials/ProfileList';

type Props = {
  actions: ProfileActionsType;
  configData: AppConfig;
  contactFormType: ContactFormType;
  customerData: Api.Customer;
  hasDisableNonEssentialCookies: boolean;
  hasGdprCookieFeatureFlag: boolean;
  languageCode: string;
  panelState?: boolean;
  payments: WebOrder.State.Payment[];
  savedCardCount: number;
  translate: any;
};

const useStyles = makeStyles((theme: Theme) => ({
  customerProfilePartial: {
    padding: theme.spacing(3, 0),
  },

  languageSelect: {
    '& .fd-icon': {
      position: 'absolute',
      right: 0,
      pointerEvents: 'none',
    },
  },

  profileList: {
    padding: theme.spacing(0, 3, 0, 2),
    width: '100%',

    '& .MuiListItem-root': {
      paddingBottom: theme.spacing(1.5),
      paddingTop: theme.spacing(1.5),
    },

    '& .MuiListItem-root:last-of-type': {
      borderBottomColor: 'transparent',

      '&:hover': {
        borderBottom: `1px solid ${theme.palette.primary.main}`,
      },
    },
  },

  [theme.breakpoints.up('sm')]: {
    customerProfilePartial: {},

    profileList: {
      padding: 0,
    },
  },
}));

export function CustomerProfilePartial(props: Props): JSX.Element {
  const classes = useStyles();
  const {
    actions,
    configData,
    contactFormType,
    customerData,
    hasDisableNonEssentialCookies,
    hasGdprCookieFeatureFlag,
    languageCode,
    panelState = false,
    payments,
    savedCardCount,
    translate,
  } = props;

  const [expandedState, setExpandedState] = useState(panelState);
  const [showPaymentMethodsDialog, setShowPaymentMethodsDialog] =
    useState(false);
  const [showAddCardDialog, setShowAddCardDialog] = useState(false);

  const theme = useTheme();
  const isDesktopLayout = useMediaQuery(theme.breakpoints.up('lg'));
  const hasCompactMenu = useMediaQuery(theme?.breakpoints.down('sm'));

  const handleOpenAddCardDialog = (): void => {
    setShowPaymentMethodsDialog(false);
    setShowAddCardDialog(true);
  };

  const toggleExpandedState = (): void => {
    setExpandedState(!expandedState);

    if (!isDesktopLayout && !expandedState) {
      menuUtils.handleScrollToSection('profile-panel', hasCompactMenu);
    }
  };

  const sectionNameElement = (
    <Typography data-fd="menu-customer-profile-title" variant="h3">
      {translate('Menu_Profile_Title')}
    </Typography>
  );

  return (
    <>
      <ExpandSection
        expanded={expandedState}
        className={classes.customerProfilePartial}
        id="profile-panel"
        onChange={toggleExpandedState}
        sectionDescription={customerData.CustomerPhoneNumberLocalFormatString}
        sectionName={sectionNameElement}
        TransitionProps={{
          mountOnEnter: true,
          timeout: 0,
          unmountOnExit: true,
        }}
        translate={translate}
      >
        <ProfileList
          className={classes.profileList}
          actions={actions}
          configData={configData}
          contactFormType={contactFormType}
          customerData={customerData}
          hasDisableNonEssentialCookies={hasDisableNonEssentialCookies}
          hasGdprCookieFeatureFlag={hasGdprCookieFeatureFlag}
          languageCode={languageCode}
          onOpenPayments={() => setShowPaymentMethodsDialog(true)}
          translate={translate}
        />
      </ExpandSection>

      <Suspense fallback={<LoadingSpinner />}>
        <PaymentMethodsDialog
          addCard={handleOpenAddCardDialog}
          deletePaymentMethod={actions.onDeletePaymentMethod}
          payments={payments}
          setShowDialog={setShowPaymentMethodsDialog}
          showDialog={showPaymentMethodsDialog}
          translate={translate}
        />
      </Suspense>
      <Suspense fallback={<LoadingSpinner />}>
        <AddCreditCardDialog
          addCardPayment={actions.onAddPaymentCard}
          cancelAddCardBridge={actions.onCancelAddCardBridge}
          clearBasketStatus={actions.onClearBasketStatus}
          configData={configData}
          currentLanguage={languageCode}
          isFromCheckoutPage={false}
          savedCardCount={savedCardCount}
          setShowDialog={setShowAddCardDialog}
          showDialog={showAddCardDialog}
          translate={translate}
        />
      </Suspense>
    </>
  );
}
